/** Which product features this client can see (GET /api/features). All false until an admin switches one on. */
import { useEffect } from 'react';
import type { FeatureAccess } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { useMe } from './session';

const NONE: FeatureAccess = { copy_trading: false, smart_portfolios: false, meme: false, master_accounts: false, volume_rewards: false };
const featuresStore = createStore<{ userId: string | null; access: FeatureAccess }>({ userId: null, access: NONE });

export async function reloadFeatures(userId: string) {
  try {
    const access = await api.get<FeatureAccess>('/api/features');
    featuresStore.set({ userId, access });
  } catch {
    featuresStore.set({ userId, access: NONE });
  }
}

/** Feature access plus whether it has loaded for the signed-in client. */
export function useFeatureState(): { ready: boolean; access: FeatureAccess } {
  const me = useMe();
  const id = me?.id ?? null;
  const s = useStore(featuresStore, (x) => x);
  useEffect(() => {
    if (id && s.userId !== id) void reloadFeatures(id);
    if (!id && s.userId) featuresStore.set({ userId: null, access: NONE });
  }, [id, s.userId]);
  const ready = !!id && s.userId === id;
  return { ready, access: ready ? s.access : NONE };
}

export const useFeatures = (): FeatureAccess => useFeatureState().access;
